import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../auth/authStore'
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  toggleBlockUser,
  updateUserCities,
  updateAssistantManagers,
} from '../../shared/api/users.api'
import type { UserAccount } from '../../shared/api/users.api'
import { getClientCities } from '../../shared/api/clients.api'
import { PageLoader } from '../../shared/ui/Spinner'
import { AccountModal } from './components/AccountModal'
import type { AccountFormData } from './components/AccountModal'
import { UserRow, ROLE_LABELS } from './components/UserRow'
import { ColumnFilterDropdown } from './components/ColumnFilterDropdown'
import { DeleteAccountModal } from './components/DeleteAccountModal'

// MANAGER добавлен 27.04.2026 — менеджеру тоже ставим город (фильтр идёт от него).
const ROLES_WITH_CITY = new Set(['MANAGER', 'PROCUREMENT', 'ROP', 'ROS'])

function cityLabel(account: UserAccount): string {
  if (account.role === 'ASSISTANT') {
    const n = (account.assistantManagerIds ?? []).length
    return n === 0 ? 'Не прикреплён' : `Менеджеров: ${n}`
  }
  if (!ROLES_WITH_CITY.has(account.role)) return '—'
  const cities = account.cities ?? []
  const exclude = account.citiesExcludeMode ?? false
  if (cities.length === 0) return exclude ? 'Все города' : 'Все города'
  if (cities.length === 1) return cities[0]!
  return `${cities.length} ${exclude ? 'города искл.' : 'города'}`
}

function statusLabel(account: UserAccount): string {
  return account.isBlocked ? 'Заблокирован' : 'Активен'
}

export function AccountsPage() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<UserAccount | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<UserAccount | null>(null)
  const [modalError, setModalError] = useState<unknown>(null)
  const [deleteError, setDeleteError] = useState<unknown>(null)

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  })

  const { data: allCities = [] } = useQuery({
    queryKey: ['client-cities'],
    queryFn: getClientCities,
  })

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['users'] })

  const createMutation = useMutation({
    mutationFn: (d: AccountFormData) =>
      createUser({ email: d.email, password: d.password, name: d.name || undefined, role: d.role }),
    onSuccess: () => {
      invalidate()
      setCreateOpen(false)
      setModalError(null)
    },
    onError: setModalError,
  })

  const updateMutation = useMutation({
    mutationFn: (d: AccountFormData & { id: string }) =>
      updateUser(d.id, {
        email: d.email || undefined,
        password: d.password || undefined,
        name: d.name || null,
        role: d.role,
      }),
    onSuccess: () => {
      invalidate()
      setEditTarget(null)
      setModalError(null)
    },
    onError: setModalError,
  })

  const citiesMutation = useMutation({
    mutationFn: ({
      id,
      cities,
      citiesExcludeMode,
    }: {
      id: string
      cities: string[]
      citiesExcludeMode: boolean
    }) => updateUserCities(id, cities, citiesExcludeMode),
    onSuccess: invalidate,
  })

  const assistantManagersMutation = useMutation({
    mutationFn: ({ id, managerIds }: { id: string; managerIds: string[] }) =>
      updateAssistantManagers(id, managerIds),
    onSuccess: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: ({ id, transferTo }: { id: string; transferTo: string | undefined }) =>
      deleteUser(id, transferTo),
    onSuccess: () => {
      invalidate()
      // Также инвалидируем дашборды и списки клиентов/заявок — перенос
      // managerId должен отразиться у нового владельца сразу.
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      void queryClient.invalidateQueries({ queryKey: ['requests'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      setDeleteTarget(null)
      setDeleteError(null)
    },
    onError: setDeleteError,
  })

  const blockMutation = useMutation({
    mutationFn: toggleBlockUser,
    onSuccess: invalidate,
  })

  const [nameFilter, setNameFilter] = useState<string[]>([])
  const [emailFilter, setEmailFilter] = useState<string[]>([])
  const [roleFilter, setRoleFilter] = useState<string[]>([])
  const [cityFilter, setCityFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string[]>([])

  const sorted = useMemo(() => {
    const ORDER: Record<string, number> = {
      ADMIN: 0,
      GENERAL_DIRECTOR: 1,
      ROP: 2,
      ROS: 3,
      MANAGER: 4,
      PROCUREMENT: 5,
      ASSISTANT: 6,
      ACCOUNTANT: 7,
      LOGIST: 7,
      WAREHOUSE_HEAD: 8,
      WAREHOUSE_QC: 8,
      WAREHOUSE_KEEPER: 8,
    }
    return [...accounts].sort((a, b) => (ORDER[a.role] ?? 9) - (ORDER[b.role] ?? 9))
  }, [accounts])

  // Кандидаты на прикрепление к помощнику: активные менеджеры и РОПы.
  const managerOptions = useMemo(
    () =>
      sorted
        .filter((a) => (a.role === 'MANAGER' || a.role === 'ROP') && !a.isBlocked)
        .map((a) => ({ id: a.id, name: a.name, email: a.email, role: a.role })),
    [sorted],
  )

  // Опции фильтров считаем от полного отсортированного списка —
  // чтобы применение одного фильтра не схлопывало варианты в других.
  const nameOptions = useMemo(
    () =>
      Array.from(new Set(sorted.map((a) => a.name || '(без имени)')))
        .sort()
        .map((v) => ({ value: v, label: v })),
    [sorted],
  )
  const emailOptions = useMemo(
    () =>
      sorted
        .map((a) => ({ value: a.email, label: a.email }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [sorted],
  )
  const roleOptions = useMemo(() => {
    const ORDER: Record<string, number> = {
      ADMIN: 0,
      GENERAL_DIRECTOR: 1,
      ROP: 2,
      ROS: 3,
      MANAGER: 4,
      PROCUREMENT: 5,
      ASSISTANT: 6,
      ACCOUNTANT: 7,
      LOGIST: 7,
      WAREHOUSE_HEAD: 8,
      WAREHOUSE_QC: 8,
      WAREHOUSE_KEEPER: 8,
    }
    return Array.from(new Set(sorted.map((a) => a.role)))
      .sort((a, b) => (ORDER[a] ?? 9) - (ORDER[b] ?? 9))
      .map((r) => ({ value: r, label: ROLE_LABELS[r] ?? r }))
  }, [sorted])
  const cityOptions = useMemo(
    () =>
      Array.from(new Set(sorted.map((a) => cityLabel(a))))
        .sort()
        .map((v) => ({ value: v, label: v })),
    [sorted],
  )
  const statusOptions = useMemo(
    () => [
      { value: 'Активен', label: 'Активен' },
      { value: 'Заблокирован', label: 'Заблокирован' },
    ],
    [],
  )

  const handleDelete = (account: UserAccount) => {
    setDeleteError(null)
    setDeleteTarget(account)
  }

  // Генеральный директор видит список аккаунтов в режиме просмотра.
  const readOnly = user?.role === 'GENERAL_DIRECTOR'
  if (user?.role !== 'ADMIN' && !readOnly) {
    return <p className="text-sm text-gray-500">Нет доступа</p>
  }

  // Пустой массив = фильтр неактивен (показываем всё). Непустой = только выбранные.
  const passes = (values: string[], current: string) =>
    values.length === 0 || values.includes(current)

  const visible = sorted.filter(
    (a) =>
      passes(nameFilter, a.name || '(без имени)') &&
      passes(emailFilter, a.email) &&
      passes(roleFilter, a.role) &&
      passes(cityFilter, cityLabel(a)) &&
      passes(statusFilter, statusLabel(a)),
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-semibold text-gray-900">Аккаунты</h1>
        {!readOnly && (
          <button
            onClick={() => {
              setModalError(null)
              setCreateOpen(true)
            }}
            className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
          >
            + Создать аккаунт
          </button>
        )}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : (
        <div className="table-base overflow-x-auto">
          <table className="text-sm">
            <thead>
              <tr>
                <th className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span>Имя</span>
                    <ColumnFilterDropdown
                      options={nameOptions}
                      selected={nameFilter}
                      onChange={setNameFilter}
                    />
                  </div>
                </th>
                <th className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span>Email</span>
                    <ColumnFilterDropdown
                      options={emailOptions}
                      selected={emailFilter}
                      onChange={setEmailFilter}
                    />
                  </div>
                </th>
                <th className="px-4 py-3">Пароль</th>
                <th className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span>Роль</span>
                    <ColumnFilterDropdown
                      options={roleOptions}
                      selected={roleFilter}
                      onChange={setRoleFilter}
                    />
                  </div>
                </th>
                <th className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span>Город / Менеджеры</span>
                    <ColumnFilterDropdown
                      options={cityOptions}
                      selected={cityFilter}
                      onChange={setCityFilter}
                    />
                  </div>
                </th>
                <th className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span>Статус</span>
                    <ColumnFilterDropdown
                      options={statusOptions}
                      selected={statusFilter}
                      onChange={setStatusFilter}
                    />
                  </div>
                </th>
                <th className="px-4 py-3">Действия</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-400">
                    Нет аккаунтов под выбранные фильтры
                  </td>
                </tr>
              ) : (
                visible.map((account) => (
                  <UserRow
                    key={account.id}
                    account={account}
                    currentUserId={user.id}
                    allCities={allCities}
                    managerOptions={managerOptions}
                    readOnly={readOnly}
                    onEdit={setEditTarget}
                    onDelete={handleDelete}
                    onToggleBlock={(a) => blockMutation.mutate(a.id)}
                    onSaveCities={(id, cities, citiesExcludeMode) =>
                      citiesMutation.mutate({ id, cities, citiesExcludeMode })
                    }
                    onSaveAssistantManagers={(id, managerIds) =>
                      assistantManagersMutation.mutate({ id, managerIds })
                    }
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <AccountModal
          isEdit={false}
          onClose={() => setCreateOpen(false)}
          onSave={(d) => createMutation.mutate(d)}
          saving={createMutation.isPending}
          error={modalError}
        />
      )}

      {editTarget && (
        <AccountModal
          isEdit
          initial={{ email: editTarget.email, name: editTarget.name ?? '', role: editTarget.role }}
          onClose={() => setEditTarget(null)}
          onSave={(d) => updateMutation.mutate({ id: editTarget.id, ...d })}
          saving={updateMutation.isPending}
          error={modalError}
        />
      )}

      {deleteTarget && (
        <DeleteAccountModal
          account={deleteTarget}
          onClose={() => {
            setDeleteTarget(null)
            setDeleteError(null)
          }}
          onConfirm={(transferTo) => deleteMutation.mutate({ id: deleteTarget.id, transferTo })}
          saving={deleteMutation.isPending}
          error={deleteError}
        />
      )}
    </div>
  )
}
